import React from "react";
import { motion } from "framer-motion";

const Privacy = () => {
  return (
    <div className="content-page">
      {/* Hero Banner */}
      <div className="content-page-header">
        <div className="absolute inset-0 bg-hero-mesh" />
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-extrabold mb-3"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Privacy Statement
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-white/60 text-sm"
          >
            Effective date: May 1, 2024 · Last updated: May 2025
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="content-page-body pb-16">
        <div className="content-card">
          <p className="text-muted mb-8 leading-relaxed">
            This Privacy Statement describes how ClockIn Master ("we", "us", "our") collects and manages your personal information when you use our Services.
          </p>

          <Section title="1. Scope">
            <p className="text-gray-600 mb-4">This Privacy Statement covers how we collect, use, store, and disclose personal information for:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-600">
              <li><strong>Account Holders:</strong> Organizations or companies that sign up for our Services</li>
              <li><strong>Application Users:</strong> Employees or team members who use our platform</li>
              <li><strong>Website Visitors:</strong> Individuals who visit our website</li>
            </ul>
          </Section>

          <Section title="2. Information We Collect">
            <div className="overflow-x-auto mb-6 rounded-xl border border-gray-200">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-lightgray">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User Type</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Information Collected</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Method</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="px-5 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">Account Holders</td>
                    <td className="px-5 py-4 text-sm text-gray-600">
                      Business name, address, contact details, payment info, employee roster, shift schedules, attendance policies
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600">Direct input during setup</td>
                  </tr>
                  <tr>
                    <td className="px-5 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">Application Users</td>
                    <td className="px-5 py-4 text-sm text-gray-600">
                      Employee ID, clock-in/out timestamps & locations, break times, overtime, biometric data (optional)
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600">Mobile app & web usage</td>
                  </tr>
                  <tr>
                    <td className="px-5 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">Website Visitors</td>
                    <td className="px-5 py-4 text-sm text-gray-600">
                      IP address, browser info, page visits, form submissions, cookie data
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600">Automatic collection</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="3. How We Use Your Information">
            <BulletList items={[
              "Process and manage employee time tracking",
              "Generate accurate attendance reports",
              "Calculate payroll and overtime",
              "Monitor compliance with labor laws",
              "Improve our time tracking accuracy",
              "Ensure proper authentication and security",
            ]} />
          </Section>

          <Section title="4. Data Security">
            <p className="text-gray-600 mb-4">We implement enterprise-grade security measures including:</p>
            <BulletList items={[
              "End-to-end encryption for all time tracking data",
              "Multi-factor authentication for admin access",
              "Regular security audits and penetration testing",
              "Role-based access controls",
              "Compliance with industry security standards",
            ]} />
          </Section>

          <Section title="5. Your Rights">
            <BulletList items={[
              "Access your time tracking records",
              "Request corrections to attendance data",
              "Export your time tracking history",
              "Request account deletion",
            ]} />
          </Section>

          <Section title="6. Data Retention">
            <BulletList items={[
              "Time tracking records: 7 years (for tax compliance)",
              "Employee profiles: Duration of employment plus 2 years",
              "Account data: Until account closure plus 1 year",
              "Analytics data: 2 years",
              "Backup data: 30 days",
            ]} />
          </Section>

          <Section title="7. Contact Us">
            <p className="text-gray-600 mb-3">For privacy-related inquiries:</p>
            <div className="bg-lightgray rounded-xl p-5 text-sm text-gray-600 space-y-1">
              <p><strong>Email:</strong> hello@clockinmaster.com</p>
              <p><strong>Phone:</strong> +44 7407822792</p>
              <p><strong>Company:</strong> PIVOTIUM LTD</p>
              <p><strong>Address:</strong> 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ</p>
              <p><strong>D-U-N-S®:</strong> 231644482</p>
              <a href="https://find-and-update.company-information.service.gov.uk/company/15612152" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline inline-block mt-1">
                Company Registration Details →
              </a>
            </div>
          </Section>

          <Section title="8. Changes to This Statement">
            <p className="text-gray-600">
              We may update this Privacy Statement periodically. We will notify you of any material changes through our website or via email.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-8 last:mb-0">
    <h2 className="text-xl font-bold text-gray-900 mb-4">{title}</h2>
    {children}
  </div>
);

const BulletList = ({ items }) => (
  <ul className="space-y-2 mb-4">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2.5 text-gray-600 text-sm">
        <span className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 flex-shrink-0" />
        {item}
      </li>
    ))}
  </ul>
);

export default Privacy;